import { useBoolean } from '@lfg/core/hooks';
import { useCallback, useState } from 'react';

import { getProjectByIdV2 } from '@/actions/project.action';
import { ProjectSchemaType } from '@/global/schemas/project.schema';
import type { ProjectSG } from '@/global/types/project.type';
import type { ProjectV2Hook } from '@/hooks/types/project.type';
import { setProject as setProjectRedux } from '@/store/actions/project.action';
import { useAppDispatch } from '@/store/utils';

const useProjectV2 = (): ProjectV2Hook => {
  const { value: loading, setTrue: setLoadingTrue, setFalse: setLoadingFalse } = useBoolean(false);
  const dispatch = useAppDispatch();
  const [error, setError] = useState<Error | undefined>(undefined);
  const [projectInfo, setProjectInfo] = useState<ProjectSG | null>(null);

  const getProjectInfo = useCallback(
    async (id: string, projectData?: ProjectSchemaType) => {
      if (!id) return null;
      setLoadingTrue();
      setError(undefined);

      try {
        const projectRes = projectData ?? (await getProjectByIdV2(id, 'en'));
        if (!projectRes) {
          throw new Error('Failed to fetch project info');
        }
        const projectTiers = projectRes.tier.layer.map(layer => ({
          price: layer.price,
          cap: layer.total,
          sold: layer.sold,
        }));
        const project = {
          admin: '',
          blockNumber: '',
          blockTimestamp: '',
          description: projectRes.info.description,
          fee: '0',
          id: projectRes.sale,
          name: projectRes.name,
          nft: projectRes.nft,
          projectImage: projectRes.node.asset,
          sold: projectRes.tier.sold,
          termsOfServiceLink: '',
          transactionHash: '',
          uri: '',
          website: projectRes.info.url,
          border: [],
          price: [],
          totalCap: projectRes.tier.nodeSupplyCap || projectTiers.reduce((acc, tier) => acc + tier.cap, 0),
          currentTierIndex: projectRes.tier.index,
          nodeAvailability: projectRes.tier.nodeSupplyCap - projectRes.tier.sold,
          tiers: projectRes.tier.layer.map(layer => ({
            price: layer.price,
            cap: layer.total,
            sold: layer.sold,
          })),
          currentTier: projectTiers[projectRes.tier.index],
          nftImage: '',
          nftImageHash: '',
          defaultToken: projectRes.setting?.defaultToken,
        } as unknown as ProjectSG;
        setProjectInfo(project);
        dispatch(setProjectRedux(project));
        return project;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch project info');
        setError(error);
        dispatch(setProjectRedux(null));
        return null;
      } finally {
        setLoadingFalse();
      }
    },
    [dispatch, setLoadingTrue, setLoadingFalse],
  );

  return {
    loading,
    error,
    projectInfo,
    getProjectInfo,
  };
};

export { useProjectV2 };
